import * as Sentry from "@sentry/react";
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from "web-vitals";

/**
 * Web Vitals reporting.
 *
 * Sentry's browserTracingIntegration already charts LCP/CLS/INP/FCP/TTFB in the
 * Performance → Web Vitals dashboard. This adds the thing the dashboard cannot:
 * it attaches the current user's vitals to every error report, so when an error
 * is investigated you can see whether the page was already janky (e.g. LCP 4.2s
 * / "poor") for that session.
 *
 * It deliberately does NOT send a Sentry event per metric — that would flood the
 * issues feed. Ratings ride along as low-cardinality tags, raw values as
 * context, plus a breadcrumb (warning-level when a metric is rated "poor").
 */
const collected: Record<string, number> = {};

const handleMetric = (metric: Metric): void => {
  collected[metric.name] = Math.round(metric.value);

  // Low-cardinality rating ("good" | "needs-improvement" | "poor") — safe as a
  // tag and useful for filtering errors by page health.
  Sentry.setTag(`web_vital.${metric.name.toLowerCase()}`, metric.rating);
  // Numeric values are high-cardinality, so keep them in context, not tags.
  Sentry.setContext("web_vitals", collected);
  Sentry.addBreadcrumb({
    category: "web-vital",
    data: { id: metric.id, navigationType: metric.navigationType },
    level: metric.rating === "poor" ? "warning" : "info",
    message: `${metric.name} ${Math.round(metric.value)}ms (${metric.rating})`
  });
};

/**
 * Register Web Vitals listeners. Call once after Sentry.init(). Each library
 * callback finalises at the right moment (e.g. CLS/INP on page hide), so this
 * only needs to run a single time at startup.
 */
export const reportWebVitals = (): void => {
  onCLS(handleMetric);
  onFCP(handleMetric);
  onINP(handleMetric);
  onLCP(handleMetric);
  onTTFB(handleMetric);
};
